import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import EnrollmentStatusUpdater from "@/components/admin/EnrollmentStatusUpdater";
import { useQuery } from "@apollo/client";
import { GET_TRAINING_BY_ID_ADMIN, GET_TRAINING_IMAGE } from "@/lib/graphql/queries";
import { Button } from "@/components/ui/button";
import {
  Calendar,
  Clock,
  Globe,
  Award,
  Users,
  Star,
  ArrowLeft,
  MapPin,
  BookOpen,
  Beaker,
  FileText,
  ImageIcon,
  Pencil,
  Trash2,
  Eye,
  CheckCircle,
  Loader2,
  Share,
  Phone,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { RootState } from "@/store";
import { fetchTrainingEnrollments, updateEnrollmentSuccess } from "@/features/training/trainingSlice";
import { UPDATE_ENROLLMENT_STATUS } from "@/lib/graphql/mutations/enrollmentMutations";
import apolloClient from "@/lib/apollo-client";
import { getCookie } from "@/utils/cookies";
import { UpdateEnrollmentInput } from "@/services/enrollmentService";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
// Reusable Content Section Component
interface ContentSectionProps {
  items: Array<{ title?: string; name?: string; description: string }>;
  icon: React.ElementType;
  emptyMessage: string;
}

const ContentSection = ({ items, icon: Icon, emptyMessage }: ContentSectionProps) => (
  items?.length > 0 ? (
    <div className="space-y-4">
      {items.map((item, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
        >
          <Card className="shadow-md hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center gap-4">
              <div className="bg-primary/10 rounded-full p-2">
                <Icon className="h-5 w-5 text-primary" />
              </div>
              <CardTitle>{item.title || item.name}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-muted-foreground whitespace-pre-wrap">{item.description}</div>
            </CardContent>
          </Card>
        </motion.div>
      ))}
    </div>
  ) : (
    <Card className="shadow-md">
      <CardContent className="flex flex-col items-center justify-center py-12">
        <Icon className="h-12 w-12 text-muted-foreground mb-4 opacity-50" />
        <p className="text-muted-foreground">{emptyMessage}</p>
      </CardContent>
    </Card>
  )
);

// Loading Skeleton Component
const LoadingSkeleton = () => (
  <div className="container mx-auto px-4 py-12 max-w-7xl">
    <Skeleton className="h-10 w-32 mb-8" />
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-7 space-y-6">
        <Skeleton className="h-12 w-3/4" />
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
      <div className="lg:col-span-5 space-y-6">
        <Skeleton className="h-80 w-full rounded-xl" />
        <Skeleton className="h-96 w-full rounded-xl" />
      </div>
    </div>
  </div>
);

// Error State Component
const ErrorState = ({ error }) => (
  <div className="container mx-auto px-4 py-12 max-w-7xl">
    <Card className="shadow-lg border-l-4 border-l-destructive">
      <CardContent className="pt-6 text-center">
        <h3 className="text-xl font-semibold text-destructive mb-2">Error Loading Training</h3>
        <p className="text-muted-foreground">{error.message}</p>
      </CardContent>
    </Card>
  </div>
);

// Not Found State Component
const NotFoundState = () => (
  <div className="container mx-auto px-4 py-12 max-w-7xl">
    <Card className="shadow-lg">
      <CardContent className="text-center py-12">
        <h3 className="text-2xl font-semibold mb-2">Training Not Found</h3>
        <p className="text-muted-foreground">This training may have been removed or doesn’t exist.</p>
      </CardContent>
    </Card>
  </div>
);

// Training Image Component
const TrainingImage = ({ filename, alt, className }) => {
  const { loading, error, data } = useQuery(GET_TRAINING_IMAGE, {
    variables: { filename },
    skip: !filename || filename.startsWith("http"),
  });

  if (loading) return <Skeleton className={`${className} rounded-xl shadow-lg`} />;
  if (error || !data) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-100 rounded-xl`}>
        <ImageIcon className="text-gray-400 h-12 w-12" />
      </div>
    );
  }

  const src = filename.startsWith("http")
    ? filename
    : `data:${data.getTrainingImageByName?.mimetype};base64,${data.getTrainingImageByName?.base64Data}`;

  return (
    <img
      src={src}
      alt={alt}
      className={`${className} rounded-xl object-cover shadow-lg`}
      loading="lazy"
    />
  );
};

// New Enrollment Filters Component
interface EnrollmentFiltersProps {
  filters: { status: string; search: string };
  onFilterChange: (filters: { status: string; search: string }) => void;
  totalEnrollments: number;
  loading: boolean;
}

const EnrollmentFilters = ({ filters, onFilterChange, totalEnrollments, loading }: EnrollmentFiltersProps) => {
  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFilterChange({ ...filters, status: e.target.value });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ ...filters, search: e.target.value });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // Trigger search on Enter key
      e.preventDefault();
      // No need to do anything else as the filter is already applied on change
    }
  };

  return (
    <Card className="mb-6 overflow-hidden">
      <CardContent className="p-0">
        <div className="p-4 bg-muted/30 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Enrollment Management</h3>
            <Badge variant="outline" className="bg-primary/5">
              {loading ? (
                <span className="flex items-center">
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  Loading...
                </span>
              ) : (
                `${totalEnrollments} Total Enrollments`
              )}
            </Badge>
          </div>
        </div>

        <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <label htmlFor="status-filter" className="text-sm font-medium text-muted-foreground">Status</label>
            <div className="relative">
              <select
                id="status-filter"
                value={filters.status}
                onChange={handleStatusChange}
                className="w-full p-2 border rounded-md bg-background text-sm focus:ring-2 focus:ring-primary pr-8 appearance-none"
              >
                <option value="">All Statuses</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
                <option value="COMPLETED">Completed</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>

          <div className="space-y-2 md:col-span-2">
            <label htmlFor="search-filter" className="text-sm font-medium text-muted-foreground">Search</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <input
                id="search-filter"
                type="text"
                placeholder="Search by name or email"
                value={filters.search}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                className="w-full pl-9 p-2 border rounded-md bg-background text-sm focus:ring-2 focus:ring-primary"
              />
              {filters.search && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1 h-6 w-6 text-muted-foreground hover:text-foreground"
                  onClick={() => onFilterChange({ ...filters, search: "" })}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {(filters.status || filters.search) && (
          <div className="px-4 py-3 bg-muted/20 border-t flex items-center justify-between">
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-xs text-muted-foreground">Active filters:</span>

              {filters.status && (
                <Badge variant="outline" className="bg-primary/5 flex items-center gap-1">
                  Status: {filters.status}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-4 w-4 ml-1 hover:bg-transparent p-0"
                    onClick={() => onFilterChange({ ...filters, status: "" })}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              )}

              {filters.search && (
                <Badge variant="outline" className="bg-primary/5 flex items-center gap-1">
                  Search: {filters.search}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-4 w-4 ml-1 hover:bg-transparent p-0"
                    onClick={() => onFilterChange({ ...filters, search: "" })}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              )}
            </div>

            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7 px-2"
              onClick={() => onFilterChange({ status: "", search: "" })}
            >
              Clear All
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Enhanced Pagination Component
interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination = ({ currentPage, totalPages, onPageChange }: PaginationProps) => {
  // Function to generate page numbers with ellipsis for large page counts
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5; // Show at most 5 page numbers

    if (totalPages <= maxPagesToShow) {
      // If we have 5 or fewer pages, show all of them
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always show first page
      pageNumbers.push(1);

      // Calculate start and end of the middle section
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      // Adjust if we're near the beginning
      if (currentPage <= 3) {
        endPage = 4;
      }

      // Adjust if we're near the end
      if (currentPage >= totalPages - 2) {
        startPage = totalPages - 3;
      }

      // Add ellipsis after first page if needed
      if (startPage > 2) {
        pageNumbers.push('...');
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      // Add ellipsis before last page if needed
      if (endPage < totalPages - 1) {
        pageNumbers.push('...');
      }

      // Always show last page
      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  if (totalPages <= 1) return null;

  return (
    <div className="flex justify-center items-center mt-8 gap-1">
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1}
        className="h-8 px-3"
      >
        <ChevronLeft className="h-4 w-4 mr-1" />
        Prev
      </Button>

      <div className="flex items-center gap-1 mx-2">
        {getPageNumbers().map((page, index) => (
          typeof page === 'number' ? (
            <Button
              key={index}
              variant={page === currentPage ? "default" : "outline"}
              size="sm"
              onClick={() => onPageChange(page)}
              className={`h-8 w-8 p-0 ${page === currentPage ? 'pointer-events-none' : ''}`}
            >
              {page}
            </Button>
          ) : (
            <span key={index} className="px-1 text-muted-foreground">{page}</span>
          )
        ))}
      </div>

      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
        className="h-8 px-3"
      >
        Next
        <ChevronRight className="h-4 w-4 ml-1" />
      </Button>
    </div>
  );
};

// Main Component
// Helper function to get badge class based on status
const getStatusBadgeClass = (status: string): string => {
  // Handle case where status might be undefined or null
  if (!status) return 'bg-gray-100 text-gray-800 border-gray-200';

  // Normalize status to uppercase for consistent comparison
  const normalizedStatus = status.toUpperCase();

  switch (normalizedStatus) {
    case 'APPROVED':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'PENDING':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'REJECTED':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'COMPLETED':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'CANCELLED':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    default:
      console.log('Unknown status:', status);
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const AdminTrainingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({ status: "", search: "" });
  const [statusUpdateSuccess, setStatusUpdateSuccess] = useState<number | string | null>(null);

  // Create a ref to track if this is the first render and to store previous filter values
  const isFirstRender = useRef(true);
  const prevFiltersRef = useRef(filters);
  const prevPageRef = useRef(currentPage);

  const { loading, error, data } = useQuery(GET_TRAINING_BY_ID_ADMIN, {
    variables: { id: parseInt(id || "0", 10) },
    skip: !id,
  });

  const {
    enrollments,
    enrollmentLoading,
    enrollmentError,
    enrollmentUpdateLoading,
    paginationMeta
  } = useSelector((state: RootState) => state.training);

  // Initial data fetch
  useEffect(() => {
    if (id && isFirstRender.current) {
      dispatch(
        fetchTrainingEnrollments({
          trainingId: parseInt(id, 10),
          status: filters.status,
          search: filters.search,
          page: currentPage,
          limit: 10,
        }) as any
      );
      isFirstRender.current = false;
    }
  }, [dispatch, id]);

  // Handle filter changes
  useEffect(() => {
    // Skip the first render
    if (isFirstRender.current) {
      return;
    }

    // Check if filters have actually changed
    if (
      JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters) ||
      prevPageRef.current !== currentPage
    ) {
      // Update refs with current values
      prevFiltersRef.current = filters;
      prevPageRef.current = currentPage;

      // Only dispatch if we're not already loading
      if (!enrollmentLoading) {
        dispatch(
          fetchTrainingEnrollments({
            trainingId: parseInt(id, 10),
            status: filters.status,
            search: filters.search,
            page: currentPage,
            limit: 10,
          }) as any
        );
      }
    }
  }, [dispatch, id, filters, currentPage, enrollmentLoading]);

  const handleEdit = () => {
    navigate(`/admin/trainings/${id}/edit`);
  };

  const handleDelete = () => {
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      // Implement delete logic here
      toast({ title: "Training Deleted", description: "Training has been successfully deleted" });
      navigate("/admin/trainings");
    } catch (err) {
      toast({
        title: "Delete Failed",
        description: "An error occurred while deleting the training",
      });
    } finally {
      setIsDeleteDialogOpen(false);
    }
  };

  const handleViewPublic = () => {
    navigate(`/courses/${id}`);
  };

  const handleFilterChange = (newFilters: { status: string; search: string }) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle enrollment status change - simplified direct approach
  const handleStatusChange = async (enrollmentId: number | string, newStatus: string) => {
    try {
      // Ensure the ID is a number
      const numericId = typeof enrollmentId === 'string' ? parseInt(enrollmentId, 10) : enrollmentId;

      // Set the enrollment ID as being updated
      setStatusUpdateSuccess(enrollmentId);

      console.log(`Updating enrollment ${numericId} to status: ${newStatus}`);

      // Create the input for the mutation
      const input: UpdateEnrollmentInput = {
        id: numericId,
        status: newStatus as 'PENDING' | 'APPROVED' | 'REJECTED' | 'COMPLETED' | 'CANCELLED'
      };

      // Directly update the UI for immediate feedback
      const updatedEnrollments = enrollments.map(enrollment => {
        if (enrollment.id === numericId) {
          return { ...enrollment, status: newStatus };
        }
        return enrollment;
      });

      // Update the Redux store for UI responsiveness
      dispatch(updateEnrollmentSuccess(updatedEnrollments));

      // Get the token for authentication
      const token = getCookie('token');

      // Make the GraphQL mutation directly
      const { data } = await apolloClient.mutate({
        mutation: UPDATE_ENROLLMENT_STATUS,
        variables: { input },
        context: {
          headers: {
            Authorization: `Bearer ${token}`
          }
        },
        fetchPolicy: 'no-cache'
      });

      console.log('Enrollment update response:', data);

      if (!data || !data.updateEnrollment) {
        throw new Error('Failed to update enrollment status');
      }

      // Show success message
      toast({
        title: "Status Updated",
        description: `Enrollment status has been updated to ${newStatus}`,
        variant: "default"
      });

      // Keep the success indicator for 3 seconds
      setTimeout(() => setStatusUpdateSuccess(null), 3000);

      // Refresh the enrollments list after a delay
      setTimeout(() => {
        dispatch(
          fetchTrainingEnrollments({
            trainingId: parseInt(id, 10),
            status: filters.status,
            search: filters.search,
            page: currentPage,
            limit: 10,
          }) as any
        );
      }, 3500);
    } catch (error) {
      console.error('Error updating enrollment status:', error);
      setStatusUpdateSuccess(null);

      // Show error message
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update enrollment status",
        variant: "destructive"
      });

      // Refresh to get the correct data
      dispatch(
        fetchTrainingEnrollments({
          trainingId: parseInt(id, 10),
          status: filters.status,
          search: filters.search,
          page: currentPage,
          limit: 10,
        }) as any
      );
    }
  };

  if (loading) return <LoadingSkeleton />;
  if (error) return <ErrorState error={error} />;
  if (!data?.getTrainingById) return <NotFoundState />;

  const training = data.getTrainingById;
  const startDate = new Date(training.startDate);
  const formattedDate = startDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 py-4 mb-8">
        <div className="flex items-center justify-between">
          <Button asChild variant="ghost" size="sm">
            <Link to="/admin/trainings">
              <ArrowLeft className="mr-2 h-5 w-5" /> Back to Trainings Management
            </Link>
          </Button>
          <div className="flex gap-3">
            <Button variant="outline" size="sm" onClick={handleViewPublic}>
              <Eye className="mr-2 h-4 w-4" /> View Public Page
            </Button>
            <Button variant="default" size="sm" onClick={handleEdit}>
              <Pencil className="mr-2 h-4 w-4" /> Edit Training
            </Button>
            <Button variant="destructive" size="sm" onClick={handleDelete}>
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </Button>
          </div>
        </div>
      </div>

      {/* Main Grid: Details on Left, Image and Stats on Right */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left: Training Details */}
        <div className="lg:col-span-7 space-y-8">
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="text-xs bg-primary/5">{training.category}</Badge>
              <Badge variant="outline" className="text-xs bg-primary/5">{training.level}</Badge>
            </div>
            <h1 className="text-4xl font-bold tracking-tight">{training.title}</h1>
            <div className="flex flex-wrap items-center gap-6 text-sm">
              <div className="flex items-center">
                <Star className="h-5 w-5 text-yellow-500 mr-2" />
                <span className="font-medium">{training.rating || 0}/5</span>
                <span className="text-muted-foreground ml-2">({training.reviews || 0} reviews)</span>
              </div>
              <div className="flex items-center">
                <Users className="h-5 w-5 text-muted-foreground mr-2" />
                <span>{training.enrollments || 0} registrations</span>
              </div>
            </div>
          </div>

          <Card className="shadow-md">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-4">About This Training</h2>
              <div className="text-muted-foreground leading-relaxed whitespace-pre-wrap">{training.description}</div>
            </CardContent>
          </Card>

          <Card className="shadow-md">
            <CardHeader>
              <CardTitle>Instructor</CardTitle>
            </CardHeader>
            <CardContent className="flex items-center gap-4">
              <div className="bg-gradient-to-br from-primary to-primary/70 rounded-full w-12 h-12 flex items-center justify-center text-white font-bold">
                {training.instructor?.name?.charAt(0) || "?"}
              </div>
              <div>
                <h3 className="font-semibold text-lg">{training.instructor?.name || "No instructor"}</h3>
                <p className="text-muted-foreground">{training.instructor?.specialization || "No specialization"}</p>
                <p className="text-sm text-muted-foreground">{training.instructor?.email || "No email"}</p>
              </div>
            </CardContent>
          </Card>

          {training.requirements?.length > 0 && (
            <Card className="shadow-md">
              <CardHeader>
                <CardTitle>Requirements</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {training.requirements.map((req: string, index: number) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="bg-primary/10 rounded-full p-1.5 mt-1">
                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                      </div>
                      <span className="text-muted-foreground">{req}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right: Image and Info Card */}
        <div className="lg:col-span-5 space-y-6">
          <TrainingImage filename={training.image} alt={training.title} className="w-full h-80" />
          <Card className="shadow-lg border-none overflow-hidden">
            <div className="bg-gradient-to-r from-primary/10 to-transparent p-6">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-muted-foreground">Price</p>
                  <span className="text-4xl font-bold text-primary">{training.price} TND</span>
                </div>
                <Badge variant="secondary" className="text-sm px-3 py-1">{training.format}</Badge>
              </div>
            </div>
            <CardContent className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-6 text-sm">
                {[
                  { icon: Clock, label: "Duration", value: training.duration },
                  { icon: Calendar, label: "Starts", value: formattedDate },
                  { icon: Globe, label: "Language", value: training.language },
                  ...(training.certificate ? [{ icon: Award, label: "Certificate", value: "Included" }] : []),
                  ...(training.location ? [{ icon: MapPin, label: "Location", value: training.location }] : []),
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <item.icon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">{item.label}</p>
                      <p className="text-muted-foreground">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Admin-specific metadata */}
              <Card className="bg-muted/50">
                <CardContent className="p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Last Updated:</span>
                    <span>{new Date(training.updatedAt).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Enrolled Users:</span>
                    <span>{training.enrolledUserIds?.length || 0}</span>
                  </div>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Enhanced Tabs */}
      <div className="mt-12">
        <h2 className="text-2xl font-bold mb-6">Training Content</h2>
        <Tabs defaultValue="modules" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-8 p-1 bg-muted rounded-xl">
            {[
              { value: "modules", icon: BookOpen, label: `Modules (${training.modules?.length || 0})` },
              { value: "labs", icon: Beaker, label: `Labs (${training.labs?.length || 0})` },
              { value: "resources", icon: FileText, label: `Resources (${training.resources?.length || 0})` },
            ].map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="flex items-center gap-2 py-3 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-lg"
              >
                <tab.icon className="h-5 w-5" />
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="modules">
            <ContentSection items={training.modules} icon={BookOpen} emptyMessage="No modules available" />
          </TabsContent>
          <TabsContent value="labs">
            <ContentSection items={training.labs} icon={Beaker} emptyMessage="No labs available" />
          </TabsContent>
          <TabsContent value="resources">
            <ContentSection items={training.resources} icon={FileText} emptyMessage="No resources available" />
          </TabsContent>
        </Tabs>
      </div>

      {/* Enhanced Enrollments Section */}
      <div className="mt-12">
        <h2 className="text-2xl font-bold mb-6">Enrollments</h2>
        <EnrollmentFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          totalEnrollments={paginationMeta?.totalItems || 0}
          loading={enrollmentLoading}
        />
        {enrollmentLoading ? (
          <div className="flex justify-center">
            <Skeleton className="h-40 w-full max-w-md rounded-lg" />
          </div>
        ) : enrollmentError ? (
          <Card className="shadow-md border-l-4 border-l-destructive">
            <CardContent className="p-6 flex items-center justify-between">
              <p className="text-destructive">Error loading enrollments: {enrollmentError}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  dispatch(
                    fetchTrainingEnrollments({
                      trainingId: parseInt(id, 10),
                      status: filters.status,
                      search: filters.search,
                      page: currentPage,
                      limit: 10,
                    }) as any
                  )
                }
              >
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : enrollments && enrollments.length > 0 ? (
          <>
         <p className="text-sm text-muted-foreground mb-4">
        Showing {(currentPage - 1) * 10 + 1} to {Math.min(currentPage * 10, paginationMeta.totalItems)} of{" "}
        {paginationMeta.totalItems} enrollments
      </p>
      <div className="overflow-hidden rounded-lg border shadow-sm">
        <Table className="min-w-full divide-y divide-gray-200">
          <TableHeader className="bg-muted/50">
            <TableRow>
              <TableHead className="py-3 px-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">User</TableHead>
              <TableHead className="py-3 px-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</TableHead>
              <TableHead className="py-3 px-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Contact</TableHead>
              <TableHead className="py-3 px-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Enrolled Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="bg-background divide-y divide-gray-200">
            {enrollments.map((enrollment) => (
              <motion.tr
                key={enrollment.id}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="hover:bg-muted/30 transition-colors"
                layout
              >
                <TableCell className="py-4 px-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium">
                      {enrollment.user?.username?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div className="ml-4">
                      <div className="font-medium">{enrollment.user?.username || "Unknown User"}</div>
                      <div className="text-sm text-muted-foreground">{enrollment.user?.email || "No email"}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="py-4 px-4 whitespace-nowrap">
                  <EnrollmentStatusUpdater
                    enrollmentId={enrollment.id}
                    initialStatus={enrollment.status || 'PENDING'}
                    onStatusUpdated={(newStatus) => {
                      // Refresh the enrollments list after a delay
                      setTimeout(() => {
                        dispatch(
                          fetchTrainingEnrollments({
                            trainingId: parseInt(id, 10),
                            status: filters.status,
                            search: filters.search,
                            page: currentPage,
                            limit: 10,
                          }) as any
                        );
                      }, 3000);
                    }}
                  />
                </TableCell>
                <TableCell className="py-4 px-4 whitespace-nowrap">
                  <div className="text-sm">
                    {enrollment.user.mobile ? (
                      <a href={`tel:${enrollment.user.mobile}`} className="flex items-center text-primary hover:underline">
                        <Phone className="h-3.5 w-3.5 mr-1.5" />
                        {enrollment.user.mobile}
                      </a>
                    ) : (
                      <span className="text-muted-foreground italic">No phone number</span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="py-4 px-4 whitespace-nowrap text-sm">
                  <div className="flex items-center">
                    <Calendar className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                    {new Date(parseInt(enrollment.enrolledAt)).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </div>
                </TableCell>
              </motion.tr>
            ))}
          </TableBody>
        </Table>
      </div>
      <Pagination currentPage={currentPage} totalPages={paginationMeta.totalPages} onPageChange={handlePageChange} />
          </>
        ) : (
          <Card className="shadow-md">
            <CardContent className="p-8 text-center">
              <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-semibold mb-2">No enrollments found</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">There are no students enrolled in this training yet. You can manually add enrollments or share the training link with potential students.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  variant="outline"
                  className="flex items-center gap-2"
                  onClick={() => {
                    toast({
                      title: "Feature Coming Soon",
                      description: "Manual enrollment will be available in a future update.",
                      variant: "default"
                    });
                  }}
                >
                  <Users className="h-4 w-4" />
                  Add Enrollment Manually
                </Button>
                <Button
                  className="flex items-center gap-2"
                  onClick={() => {
                    // Copy the public training URL to clipboard
                    const url = `${window.location.origin}/courses/${id}`;
                    navigator.clipboard.writeText(url);
                    toast({
                      title: "Link Copied",
                      description: "Training link has been copied to clipboard",
                      variant: "default"
                    });
                  }}
                >
                  <Share className="h-4 w-4" />
                  Share Training Link
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete this training?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the training and remove all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminTrainingDetails;